import React, { useEffect, useState } from 'react';
import { Input, Modal, Select, Switch, Table, message } from 'antd';
import { api, getAdminErrorMessage } from '../lib/api';

const CHATROOM_MANAGEMENT_VIEW_KEY = 'admin_chatroom_management_view';

const categoryLabels = {
  MEDICAL: '寻医问药',
  TRAVEL: '人在旅途',
  EDUCATION: '优学优教',
  PSYCHOLOGY: '心理乐园',
  COUNSELING: '心理咨询',
  FOOD: '美食美味',
  HOME: '美目家居'
};

const buttonStyle = (variant = 'neutral', disabled = false) => {
  const variants = {
    neutral: {
      background: '#f3f6fb',
      color: '#1f2a44',
      boxShadow: 'inset 0 0 0 1px rgba(148, 163, 184, 0.22)'
    },
    primary: {
      background: 'linear-gradient(135deg, #1677ff 0%, #2457d6 100%)',
      color: '#ffffff',
      boxShadow: '0 10px 20px rgba(22, 119, 255, 0.22)'
    },
    success: {
      background: 'linear-gradient(135deg, #34c759 0%, #159947 100%)',
      color: '#ffffff',
      boxShadow: '0 10px 20px rgba(21, 153, 71, 0.2)'
    },
    danger: {
      background: 'linear-gradient(135deg, #ff5f6d 0%, #d9363e 100%)',
      color: '#ffffff',
      boxShadow: '0 10px 20px rgba(217, 54, 62, 0.2)'
    }
  };
  const style = variants[variant] || variants.neutral;
  return {
    padding: '7px 14px',
    background: style.background,
    color: style.color,
    border: 'none',
    borderRadius: '999px',
    boxShadow: disabled ? 'none' : style.boxShadow,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    opacity: disabled ? 0.7 : 1
  };
};

const pillStyle = (backgroundColor, color) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px 10px',
  backgroundColor,
  color,
  fontSize: '12px',
  fontWeight: 600,
  borderRadius: '999px'
});

const labelStyle = {
  fontSize: '12px',
  color: '#7b8794',
  fontWeight: 600,
  marginBottom: '6px'
};

const toolbarStyle = {
  display: 'flex',
  gap: '12px',
  flexWrap: 'wrap',
  alignItems: 'center',
  padding: '16px 18px',
  marginBottom: '18px',
  backgroundColor: 'rgba(255, 255, 255, 0.82)',
  borderRadius: '16px',
  boxShadow: '0 14px 30px rgba(31, 42, 68, 0.06)'
};

const filterInputStyle = {
  flex: '1 1 280px',
  minWidth: '220px'
};

const filterSelectStyle = {
  minWidth: '160px',
  height: '40px',
  padding: '0 12px',
  borderRadius: '12px',
  border: '1px solid #d6e4ff',
  backgroundColor: '#f8fbff',
  color: '#1f2a44'
};

const toolbarHintStyle = {
  marginLeft: 'auto',
  color: '#6b7280',
  fontSize: '13px',
  fontWeight: 600
};

const inputStyle = {
  height: '42px',
  border: '1px solid #d6e4ff',
  borderRadius: '12px',
  backgroundColor: '#f8fbff'
};

const fieldCardStyle = {
  padding: '12px 16px',
  backgroundColor: '#f8fbff',
  borderRadius: '14px',
  border: '1px solid #e6eefc',
  color: '#1f2a44',
  lineHeight: 1.7,
  wordBreak: 'break-word'
};

const formGridStyle = {
  display: 'grid',
  gap: '12px'
};

const toAntdSortOrder = (direction) => direction === 'asc' ? 'ascend' : 'descend';

const getSortLabel = (sortBy, sortDirection) => {
  const labels = {
    createdAt: '创建时间',
    updatedAt: '更新时间',
    nameZh: '中文名称',
    category: '分类',
    onlineUsers: '在线人数',
    totalMembers: '成员数',
    isActive: '状态'
  };
  return `${labels[sortBy] || '创建时间'}${sortDirection === 'asc' ? '升序' : '降序'}`;
};

const ReadonlyField = ({ label, value }) => (
  <div>
    <div style={labelStyle}>{label}</div>
    <div style={fieldCardStyle}>{value}</div>
  </div>
);

const EditableField = ({ label, children }) => (
  <div>
    <div style={labelStyle}>{label}</div>
    {children}
  </div>
);

const ChatRoomManagement = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [editingRoom, setEditingRoom] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(CHATROOM_MANAGEMENT_VIEW_KEY) || 'null');
      if (!saved) return;
      setKeyword(saved.keyword || '');
      setCategoryFilter(saved.categoryFilter || 'all');
      setActiveFilter(saved.activeFilter || 'all');
      setPageSize(saved.pageSize || 10);
      setSortBy(saved.sortBy || 'createdAt');
      setSortDirection(saved.sortDirection || 'desc');
    } catch (_) {
      localStorage.removeItem(CHATROOM_MANAGEMENT_VIEW_KEY);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchRooms();
    }, 250);
    return () => clearTimeout(timer);
  }, [keyword, categoryFilter, activeFilter, page, pageSize, sortBy, sortDirection]);

  useEffect(() => {
    localStorage.setItem(CHATROOM_MANAGEMENT_VIEW_KEY, JSON.stringify({
      keyword,
      categoryFilter,
      activeFilter,
      pageSize,
      sortBy,
      sortDirection
    }));
  }, [keyword, categoryFilter, activeFilter, pageSize, sortBy, sortDirection]);

  useEffect(() => {
    setPage(1);
  }, [keyword, categoryFilter, activeFilter]);

  const fetchRooms = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/chatrooms', {
        params: {
          keyword: keyword.trim() || undefined,
          category: categoryFilter === 'all' ? undefined : categoryFilter,
          active: activeFilter === 'all' ? undefined : activeFilter === 'active',
          page,
          pageSize,
          sortBy,
          sortDirection
        }
      });
      setRooms(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      message.error(getAdminErrorMessage(error, '加载聊天室失败'));
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (roomId) => {
    try {
      const response = await api.get(`/api/admin/chatrooms/${roomId}`);
      const room = response.data;
      setSelectedRoom(room);
      setEditingRoom({
        nameZh: room.nameZh || '',
        nameEn: room.nameEn || '',
        descriptionZh: room.descriptionZh || '',
        descriptionEn: room.descriptionEn || '',
        category: room.category || 'MEDICAL',
        icon: room.icon || '',
        isPublic: Boolean(room.isPublic),
        onlineUsers: Number(room.onlineUsers || 0),
        totalMembers: Number(room.totalMembers || 0),
        isActive: Boolean(room.isActive)
      });
      setDetailVisible(true);
    } catch (error) {
      message.error(error.response?.data?.message || '加载聊天室详情失败');
    }
  };

  const handleSave = async () => {
    if (!selectedRoom || !editingRoom) return;
    setSaving(true);
    try {
      const payload = {
        ...editingRoom,
        onlineUsers: Number(editingRoom.onlineUsers || 0),
        totalMembers: Number(editingRoom.totalMembers || 0)
      };
      const response = await api.put(`/api/admin/chatrooms/${selectedRoom.id}`, payload);
      setSelectedRoom(response.data);
      setEditingRoom({
        nameZh: response.data.nameZh || '',
        nameEn: response.data.nameEn || '',
        descriptionZh: response.data.descriptionZh || '',
        descriptionEn: response.data.descriptionEn || '',
        category: response.data.category || 'MEDICAL',
        icon: response.data.icon || '',
        isPublic: Boolean(response.data.isPublic),
        onlineUsers: Number(response.data.onlineUsers || 0),
        totalMembers: Number(response.data.totalMembers || 0),
        isActive: Boolean(response.data.isActive)
      });
      message.success('聊天室已保存');
      fetchRooms();
    } catch (error) {
      message.error(error.response?.data?.message || '保存聊天室失败');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (room) => {
    setStatusUpdatingId(room.id);
    try {
      await api.post(`/api/admin/chatrooms/${room.id}/active`, {
        isActive: !room.isActive
      });
      message.success(room.isActive ? '聊天室已下线' : '聊天室已上线');
      if (selectedRoom?.id === room.id) {
        setSelectedRoom((previous) => previous ? { ...previous, isActive: !previous.isActive } : previous);
        setEditingRoom((previous) => previous ? { ...previous, isActive: !previous.isActive } : previous);
      }
      fetchRooms();
    } catch (error) {
      message.error(error.response?.data?.message || '更新聊天室状态失败');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleDelete = async (room) => {
    Modal.confirm({
      title: '删除聊天室',
      content: `确定删除「${room.nameZh || room.nameEn || room.id}」吗？`,
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      centered: true,
      onOk: async () => {
        setDeletingId(room.id);
        try {
          await api.delete(`/api/admin/chatrooms/${room.id}`);
          message.success('聊天室已删除');
          if (selectedRoom?.id === room.id) {
            setDetailVisible(false);
            setSelectedRoom(null);
            setEditingRoom(null);
          }
          fetchRooms();
        } catch (error) {
          message.error(error.response?.data?.message || '删除聊天室失败');
        } finally {
          setDeletingId(null);
        }
      }
    });
  };

  const resetFilters = () => {
    setKeyword('');
    setCategoryFilter('all');
    setActiveFilter('all');
    setSortBy('createdAt');
    setSortDirection('desc');
    setPage(1);
    setPageSize(10);
    localStorage.removeItem(CHATROOM_MANAGEMENT_VIEW_KEY);
  };

  const sortLabel = getSortLabel(sortBy, sortDirection);

  const columns = [
    {
      title: '图标',
      dataIndex: 'icon',
      key: 'icon',
      width: 80,
      render: (value) => <span style={{ fontSize: '14px', color: '#94a3b8' }}>{value || '—'}</span>
    },
    {
      title: '中文名称',
      dataIndex: 'nameZh',
      key: 'nameZh',
      width: 150,
      sorter: true,
      sortOrder: sortBy === 'nameZh' ? toAntdSortOrder(sortDirection) : null
    },
    {
      title: '英文名称',
      dataIndex: 'nameEn',
      key: 'nameEn',
      width: 170,
      ellipsis: true
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 110,
      sorter: true,
      sortOrder: sortBy === 'category' ? toAntdSortOrder(sortDirection) : null,
      render: (value) => (
        <span style={pillStyle('#eef2ff', '#4f46e5')}>
          {categoryLabels[value] || value}
        </span>
      )
    },
    {
      title: '状态',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      sorter: true,
      sortOrder: sortBy === 'isActive' ? toAntdSortOrder(sortDirection) : null,
      render: (value) => (
        <span style={pillStyle(value ? '#EAFBF1' : '#FFF1F2', value ? '#157347' : '#BE123C')}>
          {value ? '已上线' : '已下线'}
        </span>
      )
    },
    {
      title: '公开',
      dataIndex: 'isPublic',
      key: 'isPublic',
      width: 90,
      render: (value) => (
        <span style={pillStyle(value ? '#ecfeff' : '#f3f4f6', value ? '#0f766e' : '#4b5563')}>
          {value ? '公开' : '私有'}
        </span>
      )
    },
    {
      title: '在线人数',
      dataIndex: 'onlineUsers',
      key: 'onlineUsers',
      width: 110,
      sorter: true,
      sortOrder: sortBy === 'onlineUsers' ? toAntdSortOrder(sortDirection) : null
    },
    {
      title: '成员数',
      dataIndex: 'totalMembers',
      key: 'totalMembers',
      width: 110,
      sorter: true,
      sortOrder: sortBy === 'totalMembers' ? toAntdSortOrder(sortDirection) : null
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 190,
      sorter: true,
      sortOrder: sortBy === 'updatedAt' ? toAntdSortOrder(sortDirection) : null
    },
    {
      title: '操作',
      key: 'action',
      width: 262,
      fixed: 'right',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => openDetail(record.id)} style={buttonStyle('primary')}>
            查看编辑
          </button>
          <button
            onClick={() => toggleActive(record)}
            disabled={statusUpdatingId === record.id}
            style={buttonStyle(record.isActive ? 'danger' : 'success', statusUpdatingId === record.id)}
          >
            {statusUpdatingId === record.id ? '处理中...' : record.isActive ? '下线' : '上线'}
          </button>
          <button
            onClick={() => handleDelete(record)}
            disabled={deletingId === record.id}
            style={buttonStyle('danger', deletingId === record.id)}
          >
            {deletingId === record.id ? '删除中...' : '删除'}
          </button>
        </div>
      )
    }
  ];

  return (
    <div style={{ padding: '8px', background: 'transparent', minHeight: '100%' }}>
      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '28px', fontWeight: 700, color: '#1f2a44', marginBottom: '6px' }}>
          聊天室管理
        </div>
        <div style={{ fontSize: '14px', color: '#6b7280' }}>
          统一维护聊天室多语言名称、简介、公开性和上下线状态，App 端会直接读取这里的数据。
        </div>
      </div>

      <div style={toolbarStyle}>
        <Input
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="搜索名称、简介、创建者 ID"
          allowClear
          style={filterInputStyle}
        />
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} style={filterSelectStyle}>
          <option value="all">全部分类</option>
          {Object.entries(categoryLabels).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)} style={filterSelectStyle}>
          <option value="all">全部状态</option>
          <option value="active">已上线</option>
          <option value="inactive">已下线</option>
        </select>
        <button onClick={resetFilters} style={buttonStyle('neutral')}>恢复默认</button>
        <div style={toolbarHintStyle}>
          当前排序：{sortLabel} · 共 {total} 条
        </div>
      </div>

      <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.92)', borderRadius: '18px', boxShadow: '0 20px 45px rgba(31, 42, 68, 0.08)', overflow: 'hidden' }}>
        <Table
          columns={columns}
          dataSource={rooms}
          rowKey="id"
          loading={loading}
          onChange={(pagination, _, sorter) => {
            const nextSorter = Array.isArray(sorter) ? sorter[0] : sorter;
            setPage(pagination.current || 1);
            setPageSize(pagination.pageSize || 10);
            if (nextSorter?.field) {
              setSortBy(String(nextSorter.field));
              setSortDirection(nextSorter.order === 'ascend' ? 'asc' : 'desc');
            } else {
              setSortBy('createdAt');
              setSortDirection('desc');
            }
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize);
            }
          }}
          scroll={{ x: 1280 }}
        />
      </div>

      <Modal
        title={selectedRoom ? `聊天室：${selectedRoom.nameZh}` : '聊天室详情'}
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button onClick={() => setDetailVisible(false)} style={buttonStyle('neutral')}>
              关闭
            </button>
            <button onClick={handleSave} disabled={saving} style={buttonStyle('primary', saving)}>
              {saving ? '保存中...' : '保存'}
            </button>
          </div>
        }
        width={720}
      >
        {selectedRoom && editingRoom && (
          <div style={formGridStyle}>
            <ReadonlyField label="聊天室 ID" value={selectedRoom.id} />
            <ReadonlyField label="创建者 ID" value={selectedRoom.creatorId || 'system'} />
            <ReadonlyField label="创建时间" value={selectedRoom.createdAt} />
            <ReadonlyField label="更新时间" value={selectedRoom.updatedAt} />

            <EditableField label="中文名称">
              <Input
                value={editingRoom.nameZh}
                onChange={(e) => setEditingRoom({ ...editingRoom, nameZh: e.target.value })}
                maxLength={20}
                style={inputStyle}
              />
            </EditableField>

            <EditableField label="英文名称">
              <Input
                value={editingRoom.nameEn}
                onChange={(e) => setEditingRoom({ ...editingRoom, nameEn: e.target.value })}
                maxLength={40}
                style={inputStyle}
              />
            </EditableField>

            <EditableField label="中文简介">
              <Input.TextArea
                rows={3}
                value={editingRoom.descriptionZh}
                onChange={(e) => setEditingRoom({ ...editingRoom, descriptionZh: e.target.value })}
              />
            </EditableField>

            <EditableField label="英文简介">
              <Input.TextArea
                rows={3}
                value={editingRoom.descriptionEn}
                onChange={(e) => setEditingRoom({ ...editingRoom, descriptionEn: e.target.value })}
              />
            </EditableField>

            <EditableField label="分类">
              <Select
                value={editingRoom.category}
                onChange={(value) => setEditingRoom({ ...editingRoom, category: value })}
                style={{ width: '100%' }}
                options={Object.entries(categoryLabels).map(([value, label]) => ({ value, label }))}
              />
            </EditableField>

            <EditableField label="图标">
              <Input
                value={editingRoom.icon}
                onChange={(e) => setEditingRoom({ ...editingRoom, icon: e.target.value })}
                maxLength={10}
                style={inputStyle}
              />
            </EditableField>

            <EditableField label="在线人数">
              <Input
                type="number"
                min={0}
                value={editingRoom.onlineUsers}
                onChange={(e) => setEditingRoom({ ...editingRoom, onlineUsers: e.target.value })}
                style={inputStyle}
              />
            </EditableField>

            <EditableField label="成员数">
              <Input
                type="number"
                min={0}
                value={editingRoom.totalMembers}
                onChange={(e) => setEditingRoom({ ...editingRoom, totalMembers: e.target.value })}
                style={inputStyle}
              />
            </EditableField>

            <EditableField label="公开聊天室">
              <Switch
                checked={editingRoom.isPublic}
                onChange={(checked) => setEditingRoom({ ...editingRoom, isPublic: checked })}
                checkedChildren="公开"
                unCheckedChildren="私有"
              />
            </EditableField>

            <EditableField label="聊天室状态">
              <Switch
                checked={editingRoom.isActive}
                onChange={(checked) => setEditingRoom({ ...editingRoom, isActive: checked })}
                checkedChildren="上线"
                unCheckedChildren="下线"
              />
            </EditableField>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ChatRoomManagement;
